import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { Pencil, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { supabase } from '../lib/supabaseClient';
import {
  fetchProfileLookups,
  formatProdi,
  hydrateProfile,
  hydrateProfiles,
} from '../lib/profileLookups';
import { fetchKriteriaList } from '../lib/kriteriaMetadata';
import { formatRoleLabel } from '../lib/accessRules';


const emptyForm = {
  nama_lengkap: '',
  role: 'admin_prodi',
  prodi: '',
  is_active: true,
};

function UserManagementPage() {
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [lookups, setLookups] = useState({ prodiList: [] });
  const [roleOptions, setRoleOptions] = useState(['superadmin', 'admin_prodi']);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const fetchProfiles = async (lookupData = lookups) => {
    const { data, error } = await supabase
      .schema('akreditasi')
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(error.message);
    }

    setProfiles(hydrateProfiles(data || [], lookupData));
  };

  const initPage = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      navigate('/');
      return;
    }

    const { data: profileData, error } = await supabase
      .schema('akreditasi')
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error || !profileData) {
      setErrorMsg('Profile user tidak ditemukan.');
      setLoading(false);
      return;
    }

    if (!profileData.is_active) {
      setErrorMsg('Akun nonaktif.');
      setLoading(false);
      return;
    }

    if (profileData.role !== 'superadmin') {
      navigate('/dashboard');
      return;
    }

    let lookupData;
    try {
      lookupData = await fetchProfileLookups(supabase);
      setLookups(lookupData);
      const criteria = await fetchKriteriaList(supabase);
      setRoleOptions([
        'superadmin',
        'admin_prodi',
        ...criteria.map((item) => item.kode_kriteria.toLowerCase()),
      ]);
    } catch (lookupError) {
      setErrorMsg(lookupError.message);
      setLoading(false);
      return;
    }

    setProfile(hydrateProfile(profileData, lookupData));

    try {
      await fetchProfiles(lookupData);
    } catch (fetchError) {
      setErrorMsg(fetchError.message);
    }

    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    initPage();
  }, [initPage]);

  const handleEdit = (item) => {
    setEditId(item.id);
    setFormData({
      nama_lengkap: item.nama_lengkap || '',
      role: item.role || 'admin_prodi',
      prodi: item.prodiData?.kode_prodi || item.prodi || '',
      is_active: !!item.is_active,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
    setFormData(emptyForm);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccessMsg('');
    setErrorMsg('');

    if (!editId) {
      setErrorMsg('Data user belum dipilih.');
      setSaving(false);
      return;
    }

    const { error } = await supabase
      .schema('akreditasi')
      .from('profiles')
      .update({
        nama_lengkap: formData.nama_lengkap.trim(),
        role: formData.role,
        prodi: formData.prodi,
        is_active: formData.is_active,
      })
      .eq('id', editId);

    if (error) {
      setErrorMsg(error.message);
      setSaving(false);
      return;
    }

    await fetchProfiles();
    setSuccessMsg('Profile user berhasil diperbarui.');
    closeModal();
    setSaving(false);
  };

  if (loading) {
    return (
      <DashboardLayout
        title="Manajemen Pengguna"
        breadcrumbs={['Dashboard', 'Pengaturan', 'Pengguna']}
        profile={profile}
        activeMenu="settings"
      >
        <div className="page-loading-v2">
          <Spinner animation="border" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Manajemen Pengguna"
      breadcrumbs={['Dashboard', 'Pengaturan', 'Pengguna']}
      profile={profile}
      activeMenu="settings"
    >
      <Container fluid className="p-0">
        {successMsg && <Alert variant="success">{successMsg}</Alert>}
        {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

        <Card className="content-card-v2 settings-card">
          <Card.Body>
            <div className="section-head-v2 mb-3">
              <div>
                <h4 className="section-title-v2">Data Profile User</h4>
              </div>
              <div className="settings-menu-icon compact">
                <Users size={22} />
              </div>
            </div>

            <Table responsive hover className="table-v2 align-middle">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Nama Lengkap</th>
                  <th>Role</th>
                  <th>Prodi</th>
                  <th>Status</th>
                  <th>Aksi</th>
                </tr>
              </thead>

              <tbody>
                {profiles.length > 0 ? (
                  profiles.map((item, index) => (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td>{item.nama_lengkap || '-'}</td>
                      <td>
                        <Badge bg="primary">{formatRoleLabel(item)}</Badge>
                      </td>
                      <td>{item.prodi_display || item.prodi || '-'}</td>
                      <td>
                        <Badge bg={item.is_active ? 'success' : 'secondary'}>
                          {item.is_active ? 'Aktif' : 'Nonaktif'}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleEdit(item)}
                        >
                          <Pencil size={14} className="me-1" />
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      Belum ada data profile.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        <Modal show={showModal} onHide={closeModal} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Edit Profile User</Modal.Title>
          </Modal.Header>

          <Form onSubmit={handleSave}>
            <Modal.Body>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>Nama Lengkap</Form.Label>
                  <Form.Control
                    value={formData.nama_lengkap}
                    onChange={(e) =>
                      setFormData({ ...formData, nama_lengkap: e.target.value })
                    }
                    required
                  />
                </Col>

                <Col md={6}>
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                  >
                    {roleOptions.map((role) => (
                      <option value={role} key={role}>
                        {formatRoleLabel(role)}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={6}>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={String(formData.is_active)}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        is_active: e.target.value === 'true',
                      })
                    }
                  >
                    <option value="true">Aktif</option>
                    <option value="false">Nonaktif</option>
                  </Form.Select>
                </Col>

                <Col md={6}>
                  <Form.Label>Prodi</Form.Label>
                  <Form.Select
                    value={formData.prodi}
                    onChange={(e) =>
                      setFormData({ ...formData, prodi: e.target.value })
                    }
                  >
                    <option value="">Pilih Prodi</option>
                    {lookups.prodiList.map((item) => (
                      <option value={item.kode_prodi} key={item.id}>
                        {formatProdi(item)}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={closeModal}>
                Batal
              </Button>
              <Button className="save-btn-v2" type="submit" disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </DashboardLayout>
  );
}

export default UserManagementPage;
